import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  User,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  CreditCard,
  FileText,
  Upload,
  Eye,
  Download,
  XCircle,
  History,
  Package,
  Percent,
  RefreshCw,
  Check,
  X as CloseIcon,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  Building,
  Receipt,
  FileDown,
  Printer,
  Star,
  Gift
} from 'lucide-react';
import { UserMenu } from '../components/UserMenu';
import CompraOnlineVenta from '../components/tienda/admin/CompraOnlineVenta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatear precio en formato colombiano
const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

// Formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

// Estados de orden de venta
const ESTADOS_VENTA = {
  pendiente_primer_pago: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  al_dia: { label: 'Al día', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  pago_parcial: { label: 'Pago parcial', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  cuota_vencida: { label: 'Cuota vencida', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  pagada_totalmente: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  en_produccion: { label: 'En producción', color: 'bg-purple-100 text-purple-800', icon: Package },
  entregada: { label: 'Entregada', color: 'bg-zinc-100 text-zinc-800', icon: CheckCircle },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-800', icon: CloseIcon }
};

// Estados de pago
const ESTADOS_PAGO = {
  pendiente_verificar: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800' },
  verificado: { label: 'Verificado', color: 'bg-green-100 text-green-800' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800' },
  anulado: { label: 'Anulado', color: 'bg-zinc-100 text-zinc-600' }
};

// Medios de pago
const MEDIOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta_debito', label: 'Tarjeta Débito' },
  { value: 'tarjeta_credito', label: 'Tarjeta Crédito' },
  { value: 'pse', label: 'PSE' },
  { value: 'transfiya', label: 'Transfiya' },
  { value: 'billetera_digital', label: 'Nequi/Daviplata' },
  { value: 'consignacion', label: 'Consignación' },
  { value: 'otro', label: 'Otro' }
];

/**
 * Normaliza un item de venta para compatibilidad con diferentes formatos de cotización.
 * Los items de cotizaciones usan: descripcion_manual, precio_base, precio_cliente
 * Los items agregados post-venta usan: descripcion, precio_unitario, subtotal
 * Esta función unifica ambos formatos para la visualización.
 */
const normalizeItem = (item) => {
  if (!item) return null;
  
  const cantidad = item.cantidad || 1;
  
  // Determinar la descripción (prioridad: descripcion > descripcion_manual > nombre)
  const descripcion = item.descripcion || item.descripcion_manual || item.nombre || 'Sin descripción';
  
  // Determinar el precio unitario (prioridad: precio_unitario > precio_base)
  const precioUnitario = item.precio_unitario || item.precio_base || 0;
  
  // Determinar el subtotal (prioridad: subtotal > precio_cliente > precio_unitario * cantidad)
  const subtotal = item.subtotal || item.precio_cliente || (precioUnitario * cantidad);
  
  // Determinar el nombre del producto
  // Para items de cotizador, extraer tipo de prenda de la descripción o usar genérico
  let nombreProducto = item.nombre;
  if (!nombreProducto && item.tipo_item === 'cotizador') {
    // Intentar extraer el tipo de prenda de la descripción
    const descripcionLower = descripcion.toLowerCase();
    if (descripcionLower.includes('traje')) nombreProducto = 'Traje a la medida';
    else if (descripcionLower.includes('chaleco')) nombreProducto = 'Chaleco a la medida';
    else if (descripcionLower.includes('camisa')) nombreProducto = 'Camisa a la medida';
    else if (descripcionLower.includes('pantalón') || descripcionLower.includes('pantalon')) nombreProducto = 'Pantalón a la medida';
    else if (descripcionLower.includes('saco')) nombreProducto = 'Saco a la medida';
    else nombreProducto = 'Prenda a la medida';
  }
  
  return {
    ...item,
    // Campos normalizados
    descripcion: descripcion,
    nombre_display: nombreProducto || descripcion.substring(0, 40) + (descripcion.length > 40 ? '...' : ''),
    precio_unitario: precioUnitario,
    subtotal: subtotal,
    cantidad: cantidad,
    // Campos originales preservados
    descripcion_original: item.descripcion,
    descripcion_manual_original: item.descripcion_manual,
    precio_base_original: item.precio_base,
    precio_cliente_original: item.precio_cliente
  };
};

const VentaDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    planPagos: true,
    historialPagos: true,
    historial: false,
    puntosJPM: false
  });
  
  // Puntos JPM
  const [puntosInfo, setPuntosInfo] = useState(null);

  // Form states
  const [pagoForm, setPagoForm] = useState({
    valor: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    medio_pago: 'transferencia',
    banco_plataforma: '',
    numero_referencia: '',
    cuota_id: '',
    observaciones: '',
    soporte: null
  });

  const [itemForm, setItemForm] = useState({
    descripcion: '',
    cantidad: 1,
    precio_unitario: '',
    descuento_porcentaje: 0,
    es_cortesia: false,
    notas: ''
  });

  const [ajusteForm, setAjusteForm] = useState({
    tipo: 'descuento',
    descripcion: '',
    valor: '',
    motivo: ''
  });

  // Fecha de entrega
  const [showFechaEntregaModal, setShowFechaEntregaModal] = useState(false);
  const [fechaEntregaForm, setFechaEntregaForm] = useState({
    modalidad: 'automatica',
    fecha_entrega: '',
    ciudad_entrega: ''
  });

  // Configurar Plan de Pagos
  const [showConfigPlanModal, setShowConfigPlanModal] = useState(false);
  const [configPlanForm, setConfigPlanForm] = useState({
    modalidad_pago: 'anticipo_saldo',
    porcentaje_anticipo: 70,
    num_cuotas: 2
  });

  // Cargar orden
  const fetchOrden = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ventas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrden(response.data);
      
      // Obtener info de Puntos JPM
      try {
        const puntosResponse = await axios.get(`${API_URL}/api/puntos-jpm/venta/${id}/info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPuntosInfo(puntosResponse.data);
      } catch (err) {
        console.log('Puntos JPM no disponible:', err.message);
      }
    } catch (error) {
      console.error('Error al cargar orden:', error);
      toast.error('Error al cargar la orden de venta');
      navigate('/ventas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOrden();
  }, [fetchOrden]);

  // Verificar si el usuario puede eliminar items (admin/gerencia)
  const canDeleteItems = useMemo(() => {
    if (user && user.role) {
      const rol = (user.role || '').toLowerCase();
      return ['gerencia', 'admin', 'administrador'].includes(rol);
    }
    return false;
  }, [user]);

  // Eliminar item de la venta
  const handleEliminarItem = async (itemId, descripcion) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${descripcion}"?\n\nEsto recalculará los totales de la venta.`)) {
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/ventas/${id}/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.mensaje || 'Producto eliminado');
      fetchOrden();
    } catch (error) {
      console.error('Error al eliminar item:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el producto');
    } finally {
      setSubmitting(false);
    }
  };

  // Registrar pago
  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    if (!pagoForm.valor || pagoForm.valor <= 0) {
      toast.error('Ingresa un valor válido');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('valor', pagoForm.valor);
      formData.append('fecha_pago', pagoForm.fecha_pago);
      formData.append('medio_pago', pagoForm.medio_pago);
      if (pagoForm.banco_plataforma) formData.append('banco_plataforma', pagoForm.banco_plataforma);
      if (pagoForm.numero_referencia) formData.append('numero_referencia', pagoForm.numero_referencia);
      if (pagoForm.cuota_id) formData.append('cuota_id', pagoForm.cuota_id);
      if (pagoForm.observaciones) formData.append('observaciones', pagoForm.observaciones);
      if (pagoForm.soporte) formData.append('soporte', pagoForm.soporte);

      await axios.post(`${API_URL}/api/ventas/${id}/pagos`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Pago registrado exitosamente');
      setShowPagoModal(false);
      setPagoForm({
        valor: '',
        fecha_pago: new Date().toISOString().split('T')[0],
        medio_pago: 'transferencia',
        banco_plataforma: '',
        numero_referencia: '',
        cuota_id: '',
        observaciones: '',
        soporte: null
      });
      fetchOrden();
    } catch (error) {
      console.error('Error al registrar pago:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setSubmitting(false);
    }
  };

  // Verificar pago
  const handleVerificarPago = async (pagoId, aprobado) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/ventas/${id}/pagos/${pagoId}/verificar?aprobado=${aprobado}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(aprobado ? 'Pago verificado' : 'Pago rechazado');
      if (res.data?.mi_jpm_activado && orden?.cliente_id) {
        toast.info('Se creó el área Mi JPM del cliente. Su mensaje de bienvenida está listo para enviar.', {
          duration: 10000,
          action: { label: 'Enviar bienvenida', onClick: () => navigate(`/clientes/${orden.cliente_id}`) },
        });
      }
      fetchOrden();
    } catch (error) {
      console.error('Error al verificar pago:', error);
      toast.error(error.response?.data?.detail || 'Error al verificar el pago');
    }
  };

  // Agregar item
  const handleAgregarItem = async (e) => {
    e.preventDefault();
    if (!itemForm.descripcion || !itemForm.precio_unitario) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/ventas/${id}/items`, {
        tipo_item: 'manual',
        descripcion: itemForm.descripcion,
        cantidad: parseInt(itemForm.cantidad) || 1,
        precio_unitario: parseInt(itemForm.precio_unitario),
        descuento_porcentaje: parseInt(itemForm.descuento_porcentaje) || 0,
        es_cortesia: itemForm.es_cortesia,
        notas: itemForm.notas
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Producto agregado exitosamente');
      setShowItemModal(false);
      setItemForm({
        descripcion: '',
        cantidad: 1,
        precio_unitario: '',
        descuento_porcentaje: 0,
        es_cortesia: false,
        notas: ''
      });
      fetchOrden();
    } catch (error) {
      console.error('Error al agregar item:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar el producto');
    } finally {
      setSubmitting(false);
    }
  };

  // Agregar ajuste
  const handleAgregarAjuste = async (e) => {
    e.preventDefault();
    if (!ajusteForm.descripcion || !ajusteForm.valor) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      let valorFinal = parseInt(ajusteForm.valor);
      if (ajusteForm.tipo === 'descuento' || ajusteForm.tipo === 'cortesia') {
        valorFinal = -Math.abs(valorFinal);
      }

      await axios.post(`${API_URL}/api/ventas/${id}/ajustes`, {
        tipo: ajusteForm.tipo,
        descripcion: ajusteForm.descripcion,
        valor: valorFinal,
        motivo: ajusteForm.motivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Ajuste aplicado exitosamente');
      setShowAjusteModal(false);
      setAjusteForm({
        tipo: 'descuento',
        descripcion: '',
        valor: '',
        motivo: ''
      });
      fetchOrden();
    } catch (error) {
      console.error('Error al agregar ajuste:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar el ajuste');
    } finally {
      setSubmitting(false);
    }
  };

  // Actualizar fecha de entrega
  const handleActualizarFechaEntrega = async (e) => {
    e.preventDefault();
    
    // Validar que si es manual, tenga fecha
    if (fechaEntregaForm.modalidad === 'manual' && !fechaEntregaForm.fecha_entrega) {
      toast.error('Ingresa una fecha de entrega');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        modalidad_fecha_entrega: fechaEntregaForm.modalidad,
        ciudad_entrega: fechaEntregaForm.ciudad_entrega || null
      };
      
      if (fechaEntregaForm.modalidad === 'manual') {
        payload.fecha_entrega = fechaEntregaForm.fecha_entrega;
      }

      await axios.put(`${API_URL}/api/ventas/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Fecha de entrega actualizada');
      setShowFechaEntregaModal(false);
      fetchOrden();
    } catch (error) {
      console.error('Error al actualizar fecha de entrega:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la fecha de entrega');
    } finally {
      setSubmitting(false);
    }
  };

  // Abrir modal de fecha de entrega con datos actuales
  const abrirModalFechaEntrega = () => {
    setFechaEntregaForm({
      modalidad: orden?.modalidad_fecha_entrega || 'automatica',
      fecha_entrega: orden?.fecha_entrega || '',
      ciudad_entrega: orden?.ciudad_entrega || ''
    });
    setShowFechaEntregaModal(true);
  };

  // Descargar PDF de Estado de Cuenta
  const handleDescargarPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ventas/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crear URL y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener nombre del archivo del header o usar default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Estado_Cuenta_${orden?.numero || 'OV'}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };

  // Descargar PDF de Orden de Venta (para el cliente)
  const handleDescargarPDFOrden = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ventas/${id}/pdf-orden`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crear URL y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener nombre del archivo del header o usar default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Orden_Venta_${orden?.numero || 'OV'}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF de Orden de Venta descargado');
    } catch (error) {
      console.error('Error al descargar PDF de orden:', error);
      toast.error('Error al generar el PDF de Orden de Venta');
    }
  };

  // Configurar plan de pagos
  const handleConfigurarPlan = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/ventas/${id}/configurar-plan-pagos`, configPlanForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Plan de pagos configurado');
      setShowConfigPlanModal(false);
      fetchOrden();
    } catch (error) {
      console.error('Error al configurar plan:', error);
      toast.error(error.response?.data?.detail || 'Error al configurar el plan de pagos');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getEstadoConfig = (estado) => ESTADOS_VENTA[estado] || ESTADOS_VENTA.pendiente_primer_pago;
  const getPagoEstadoConfig = (estado) => ESTADOS_PAGO[estado] || ESTADOS_PAGO.pendiente_verificar;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!orden) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Orden no encontrada</p>
        </div>
      </div>
    );
  }

  const estadoConfig = getEstadoConfig(orden.estado);
  const EstadoIcon = estadoConfig.icon;
  const porcentajePagado = orden.porcentaje_pagado || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
      {/* Header */}
      <header className="bg-zinc-900/80 backdrop-blur-md border-b border-zinc-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 min-h-16 py-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate('/ventas')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors shrink-0"
                data-testid="venta-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-zinc-400" />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h1 className="text-sm sm:text-xl font-semibold text-amber-400 font-mono whitespace-nowrap" data-testid="venta-numero">{orden.numero}</h1>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${estadoConfig.color}`} data-testid="venta-estado-badge">
                    <EstadoIcon className="w-3 h-3 inline mr-1" />
                    {estadoConfig.label}
                  </span>
                </div>
                <p className="hidden sm:block text-sm text-zinc-400">Estado de cuenta</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              <button
                onClick={handleDescargarPDFOrden}
                className="flex items-center gap-2 px-2.5 sm:px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                title="Descargar PDF de Orden de Venta (para el cliente)"
                data-testid="btn-pdf-orden"
              >
                <Receipt className="w-4 h-4" />
                <span className="hidden sm:inline">Orden</span>
              </button>
              <button
                onClick={handleDescargarPDF}
                className="flex items-center gap-2 px-2.5 sm:px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                title="Descargar PDF de Estado de Cuenta"
                data-testid="btn-pdf-estado"
              >
                <FileDown className="w-4 h-4" />
                <span className="hidden sm:inline">Estado</span>
              </button>
              <button
                onClick={fetchOrden}
                className="p-1.5 sm:p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="venta-refresh-btn"
              >
                <RefreshCw className="w-5 h-5 text-zinc-400" />
              </button>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda - Info y Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Resumen financiero */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Total Venta</p>
                  <p className="text-2xl font-bold text-white">{formatPrice(orden.total_final)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Pagado</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatPrice(orden.total_pagado)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Saldo Pendiente</p>
                  <p className="text-2xl font-bold text-red-400">{formatPrice(orden.saldo_pendiente)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Avance</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${porcentajePagado >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(porcentajePagado, 100)}%` }}
                      />
                    </div>
                    <span className="text-lg font-bold text-white">{porcentajePagado}%</span>
                  </div>
                </div>
              </div>
            </div>

            <CompraOnlineVenta orden={orden} />

            {/* Items de la venta */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('items')}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Productos/Servicios</span>
                  <span className="text-sm text-zinc-400">({orden.items?.length || 0})</span>
                </div>
                {expandedSections.items ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
              </button>

              {expandedSections.items && (
                <div className="border-t border-zinc-700/50">
                  <div className="divide-y divide-zinc-700/30">
                    {orden.items?.map((rawItem, idx) => {
                      const item = normalizeItem(rawItem);
                      if (!item) return null;
                      
                      return (
                        <div key={item.id || idx} className="p-4 hover:bg-zinc-700/20">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              {/* Nombre del producto */}
                              <p className="text-white font-semibold text-base">
                                {item.nombre_display}
                              </p>
                              
                              {/* Descripción detallada */}
                              <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                                {item.descripcion}
                              </p>
                              
                              {/* Badges */}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {item.tipo_item && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    item.tipo_item === 'cotizador' 
                                      ? 'bg-blue-500/20 text-blue-300' 
                                      : 'bg-zinc-600/50 text-zinc-300'
                                  }`}>
                                    {item.tipo_item === 'cotizador' ? 'Cotizador' : 'Manual'}
                                  </span>
                                )}
                                {item.agregado_post_venta && (
                                  <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">
                                    + Agregado post-venta
                                  </span>
                                )}
                                {item.es_cortesia && (
                                  <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">
                                    Cortesía
                                  </span>
                                )}
                              </div>
                              
                              {/* Notas internas */}
                              {(item.notas || item.notas_internas) && (
                                <p className="text-xs text-zinc-500 mt-2 italic">
                                  Nota: {item.notas || item.notas_internas}
                                </p>
                              )}
                            </div>
                            
                            {/* Precio y cantidad */}
                            <div className="text-right shrink-0 flex items-start gap-2">
                              <div>
                                <p className="text-white font-medium">
                                  {item.cantidad > 1 && (
                                    <span className="text-zinc-400 text-sm">{item.cantidad} x </span>
                                  )}
                                  {formatPrice(item.precio_unitario)}
                                </p>
                                <p className="text-lg font-bold text-amber-400 mt-1">
                                  {formatPrice(item.subtotal)}
                                </p>
                              </div>
                              
                              {/* Botón eliminar - solo admin/gerencia */}
                              {canDeleteItems && orden.items?.length > 1 && (
                                <button
                                  onClick={() => handleEliminarItem(item.id, item.nombre_display || item.descripcion)}
                                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                  title="Eliminar producto"
                                  data-testid={`btn-eliminar-item-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Resumen de totales de productos */}
                  <div className="border-t border-zinc-700/50 bg-zinc-900/30 p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Subtotal ({orden.items?.length || 0} productos)</span>
                        <span className="text-white">{formatPrice(orden.subtotal || orden.items?.reduce((sum, item) => {
                          const normalizedItem = normalizeItem(item);
                          return sum + (normalizedItem?.subtotal || 0);
                        }, 0))}</span>
                      </div>
                      {orden.descuento_valor > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">Descuento ({orden.descuento_porcentaje || 0}%)</span>
                          <span className="text-red-400">-{formatPrice(orden.descuento_valor)}</span>
                        </div>
                      )}
                      {orden.total_ajustes !== 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">Ajustes</span>
                          <span className={orden.total_ajustes > 0 ? 'text-amber-400' : 'text-red-400'}>
                            {orden.total_ajustes > 0 ? '+' : ''}{formatPrice(orden.total_ajustes)}
                          </span>
                        </div>
                      )}
                      {orden.puntos_jpm_descuento > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400 flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400" />
                            Puntos JPM redimidos ({orden.puntos_jpm_a_redimir})
                          </span>
                          <span className="text-amber-400">-{formatPrice(orden.puntos_jpm_descuento)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-zinc-700/50">
                        <span className="text-white font-semibold">Total Venta</span>
                        <span className="text-xl font-bold text-amber-400">{formatPrice(orden.total_final)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="p-4 bg-zinc-900/50 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setShowItemModal(true)}
                      className="flex-1 py-2 px-4 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      data-testid="venta-agregar-producto-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Agregar Producto
                    </button>
                    <button
                      onClick={() => setShowAjusteModal(true)}
                      className="flex-1 py-2 px-4 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      data-testid="venta-agregar-ajuste-btn"
                    >
                      <Percent className="w-4 h-4" />
                      Agregar Ajuste
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Plan de Pagos */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('planPagos')}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Plan de Pagos</span>
                  {orden.modalidad_pago === 'pendiente_definir' ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">Pendiente</span>
                  ) : (
                    <span className="text-sm text-zinc-400">({orden.plan_pagos?.length || 0} cuotas)</span>
                  )}
                </div>
                {expandedSections.planPagos ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
              </button>

              {expandedSections.planPagos && (
                <div className="border-t border-zinc-700/50">
                  {/* Mostrar configuración si está pendiente */}
                  {orden.modalidad_pago === 'pendiente_definir' ? (
                    <div className="p-4 space-y-4">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <p className="text-amber-400 text-sm flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          El plan de pagos está pendiente por configurar
                        </p>
                      </div>
                      <button
                        onClick={() => setShowConfigPlanModal(true)}
                        className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors flex items-center justify-center gap-2"
                        data-testid="btn-configurar-plan"
                      >
                        <DollarSign className="w-5 h-5" />
                        Configurar Plan de Pagos
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-700/30">
                      {orden.plan_pagos?.map((cuota) => {
                        const isPagada = cuota.estado === 'pagada';
                        const isVencida = cuota.estado === 'vencida';
                        const isParcial = cuota.estado === 'parcial';

                        return (
                          <div key={cuota.id} className="p-4">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium">Cuota {cuota.numero_cuota}</span>
                                  {isPagada && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                                  {isVencida && <AlertTriangle className="w-4 h-4 text-red-400" />}
                                  {isParcial && <Clock className="w-4 h-4 text-amber-400" />}
                                </div>
                                <p className="text-sm text-zinc-400">
                                  Fecha: {formatDate(cuota.fecha_acordada)}
                                </p>
                                {cuota.observaciones && (
                                  <p className="text-xs text-zinc-500">{cuota.observaciones}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-white">{formatPrice(cuota.valor_programado)}</p>
                                {cuota.valor_abonado > 0 && (
                                  <p className="text-sm text-emerald-400">Abonado: {formatPrice(cuota.valor_abonado)}</p>
                                )}
                                {cuota.saldo_cuota > 0 && (
                                  <p className="text-sm text-red-400">Saldo: {formatPrice(cuota.saldo_cuota)}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Puntos JPM */}
            {puntosInfo && (
              <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('puntosJPM')}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                    <span className="font-semibold text-white">Puntos JPM</span>
                    {puntosInfo.elegible ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                        Elegible
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-600/50 text-zinc-400">
                        No elegible
                      </span>
                    )}
                  </div>
                  {expandedSections.puntosJPM ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>

                {expandedSections.puntosJPM && (
                  <div className="border-t border-zinc-700/50 p-4">
                    {puntosInfo.elegible ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-[#D4AF37]/10 rounded-lg p-3">
                            <p className="text-xs text-zinc-400 mb-1">Puntos generados</p>
                            <p className="text-2xl font-bold text-[#D4AF37]">{puntosInfo.puntos_generados}</p>
                          </div>
                          <div className="bg-zinc-700/30 rounded-lg p-3">
                            <p className="text-xs text-zinc-400 mb-1">Potencial total</p>
                            <p className="text-2xl font-bold text-white">{puntosInfo.puntos_potenciales}</p>
                          </div>
                        </div>
                        
                        {puntosInfo.puntos_utilizados > 0 && (
                          <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg">
                            <Gift className="w-5 h-5 text-blue-400" />
                            <div>
                              <p className="text-sm text-white">Puntos redimidos en esta compra</p>
                              <p className="text-lg font-bold text-blue-400">{puntosInfo.puntos_utilizados} puntos</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="text-xs text-zinc-500 space-y-1">
                          <p>• El cliente acumula 1 punto por cada $10,000 COP pagados</p>
                          <p>• Beneficio: 1% del valor pagado para próximas compras</p>
                          <p>• Los puntos solo pueden redimirse en compras futuras</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Star className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                        <p className="text-zinc-400 font-medium">No elegible para Puntos JPM</p>
                        <p className="text-sm text-zinc-500 mt-1">{puntosInfo.motivo}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Historial de Pagos */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('historialPagos')}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Pagos Recibidos</span>
                  <span className="text-sm text-zinc-400">({orden.pagos?.length || 0})</span>
                </div>
                {expandedSections.historialPagos ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
              </button>

              {expandedSections.historialPagos && (
                <div className="border-t border-zinc-700/50">
                  {orden.pagos?.length > 0 ? (
                    <div className="divide-y divide-zinc-700/30">
                      {orden.pagos.map((pago) => {
                        const estadoPago = getPagoEstadoConfig(pago.estado);
                        const canVerify = user?.role === 'gerencia' || user?.role === 'admin' || user?.role === 'contabilidad';

                        return (
                          <div key={pago.id} className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-amber-400 font-mono text-sm">{pago.numero_pago}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${estadoPago.color}`}>
                                    {estadoPago.label}
                                  </span>
                                </div>
                                <p className="text-white font-medium">{formatPrice(pago.valor)}</p>
                                <p className="text-sm text-zinc-400">
                                  {formatDate(pago.fecha_pago)} · {MEDIOS_PAGO.find(m => m.value === pago.medio_pago)?.label || pago.medio_pago}
                                </p>
                                {pago.banco_plataforma && (
                                  <p className="text-xs text-zinc-500">{pago.banco_plataforma}</p>
                                )}
                                {pago.numero_referencia && (
                                  <p className="text-xs text-zinc-500">Ref: {pago.numero_referencia}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {pago.soporte_path && (
                                  <a
                                    href={`${API_URL}/api/ventas/${id}/pagos/${pago.id}/soporte`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                                    title="Ver soporte"
                                  >
                                    <Eye className="w-4 h-4 text-zinc-400" />
                                  </a>
                                )}
                                {canVerify && pago.estado === 'pendiente_verificar' && (
                                  <>
                                    <button
                                      onClick={() => handleVerificarPago(pago.id, true)}
                                      className="p-2 hover:bg-emerald-500/20 rounded-lg transition-colors"
                                      title="Aprobar"
                                    >
                                      <Check className="w-4 h-4 text-emerald-400" />
                                    </button>
                                    <button
                                      onClick={() => handleVerificarPago(pago.id, false)}
                                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                      title="Rechazar"
                                    >
                                      <XCircle className="w-4 h-4 text-red-400" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-zinc-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay pagos registrados</p>
                    </div>
                  )}

                  <div className="p-4 bg-zinc-900/50">
                    <button
                      onClick={() => setShowPagoModal(true)}
                      className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors flex items-center justify-center gap-2 font-medium"
                    >
                      <Plus className="w-5 h-5" />
                      Registrar Pago
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna derecha - Info del cliente */}
          <div className="space-y-6">
            {/* Datos del cliente */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Cliente
              </h3>
              <div className="space-y-3">
                <p className="text-white font-medium text-lg">{orden.cliente_nombre}</p>
                {orden.cliente_telefono && (
                  <a 
                    href={`tel:${orden.cliente_telefono}`}
                    className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {orden.cliente_telefono}
                  </a>
                )}
                {orden.cliente_correo && (
                  <a 
                    href={`mailto:${orden.cliente_correo}`}
                    className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {orden.cliente_correo}
                  </a>
                )}
              </div>
            </div>

            {/* Info de la venta */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Detalles
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Cotización origen</span>
                  <span className="text-amber-400 font-mono">{orden.cotizacion_numero}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Fecha creación</span>
                  <span className="text-white">{formatDate(orden.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Modalidad</span>
                  <span className="text-white capitalize">{orden.modalidad_pago?.replace('_', ' ')}</span>
                </div>
                {orden.vendedor_nombre && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Vendedor</span>
                    <span className="text-white">{orden.vendedor_nombre}</span>
                  </div>
                )}
                {orden.fecha_evento && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Fecha evento</span>
                    <span className="text-white">{formatDate(orden.fecha_evento)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Fecha de Entrega */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  Fecha de Entrega
                </h3>
                <button
                  onClick={abrirModalFechaEntrega}
                  className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                  title="Editar fecha de entrega"
                  data-testid="btn-editar-fecha-entrega"
                >
                  <Edit className="w-4 h-4 text-zinc-400 hover:text-amber-400" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Fecha de entrega principal */}
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <p className="text-xs text-zinc-400 mb-1">
                    {orden.modalidad_fecha_entrega === 'manual' ? 'Fecha Manual' : 'Fecha Automática'}
                  </p>
                  <p className="text-xl font-bold text-emerald-400">
                    {orden.fecha_entrega ? formatDate(orden.fecha_entrega) : 'No definida'}
                  </p>
                  {orden.modalidad_fecha_entrega === 'automatica' && orden.fecha_evento && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Calculada 8 días antes del evento
                    </p>
                  )}
                </div>
                
                {/* Indicador de modalidad */}
                <div className="flex items-center gap-2 text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${
                    orden.modalidad_fecha_entrega === 'manual' 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {orden.modalidad_fecha_entrega === 'manual' ? 'Manual' : 'Automática'}
                  </span>
                  <span className="text-zinc-500">
                    {orden.modalidad_fecha_entrega === 'manual' 
                      ? 'La fecha fue ingresada manualmente' 
                      : 'Calculada desde la cotización'}
                  </span>
                </div>
                
                {orden.ciudad_entrega && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Building className="w-4 h-4" />
                    <span>Ciudad: {orden.ciudad_entrega}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Historial */}
            <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('historial')}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-700/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Historial</span>
                </div>
                {expandedSections.historial ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
              </button>

              {expandedSections.historial && (
                <div className="border-t border-zinc-700/50 max-h-64 overflow-y-auto">
                  {orden.historial?.map((entry, idx) => (
                    <div key={entry.id || idx} className="p-3 border-b border-zinc-700/30 last:border-0">
                      <p className="text-sm text-white">{entry.descripcion}</p>
                      <p className="text-xs text-zinc-500">
                        {entry.usuario_nombre} · {formatDateTime(entry.fecha)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modal Registrar Pago */}
      {showPagoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Registrar Pago</h3>
                <button onClick={() => setShowPagoModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                  <CloseIcon className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            <form onSubmit={handleRegistrarPago} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Valor del pago *</label>
                <input
                  type="number"
                  value={pagoForm.valor}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, valor: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Fecha del pago *</label>
                <input
                  type="date"
                  value={pagoForm.fecha_pago}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, fecha_pago: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Medio de pago *</label>
                <select
                  value={pagoForm.medio_pago}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, medio_pago: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                >
                  {MEDIOS_PAGO.map(medio => (
                    <option key={medio.value} value={medio.value}>{medio.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Banco/Plataforma</label>
                <input
                  type="text"
                  value={pagoForm.banco_plataforma}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, banco_plataforma: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Bancolombia, Nequi"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Número de referencia</label>
                <input
                  type="text"
                  value={pagoForm.numero_referencia}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, numero_referencia: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Referencia de la transacción"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Aplicar a cuota</label>
                <select
                  value={pagoForm.cuota_id}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, cuota_id: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Sin asignar a cuota específica</option>
                  {orden.plan_pagos?.filter(c => c.saldo_cuota > 0).map(cuota => (
                    <option key={cuota.id} value={cuota.id}>
                      Cuota {cuota.numero_cuota} - Saldo: {formatPrice(cuota.saldo_cuota)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Comprobante (opcional)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setPagoForm(prev => ({ ...prev, soporte: e.target.files[0] }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500 file:text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Observaciones</label>
                <textarea
                  value={pagoForm.observaciones}
                  onChange={(e) => setPagoForm(prev => ({ ...prev, observaciones: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  rows={2}
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPagoModal(false)}
                  className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5" />
                      Registrar Pago
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Agregar Item */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Agregar Producto</h3>
                <button onClick={() => setShowItemModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                  <CloseIcon className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            <form onSubmit={handleAgregarItem} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Descripción *</label>
                <input
                  type="text"
                  value={itemForm.descripcion}
                  onChange={(e) => setItemForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Nombre del producto o servicio"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Cantidad</label>
                  <input
                    type="number"
                    value={itemForm.cantidad}
                    onChange={(e) => setItemForm(prev => ({ ...prev, cantidad: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Precio unitario *</label>
                  <input
                    type="number"
                    value={itemForm.precio_unitario}
                    onChange={(e) => setItemForm(prev => ({ ...prev, precio_unitario: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="es_cortesia"
                  checked={itemForm.es_cortesia}
                  onChange={(e) => setItemForm(prev => ({ ...prev, es_cortesia: e.target.checked }))}
                  className="w-5 h-5 rounded bg-zinc-800 border-zinc-700 text-amber-500 focus:ring-amber-500"
                />
                <label htmlFor="es_cortesia" className="text-white">Es cortesía (sin costo)</label>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Notas</label>
                <input
                  type="text"
                  value={itemForm.notas}
                  onChange={(e) => setItemForm(prev => ({ ...prev, notas: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Agregar Ajuste */}
      {showAjusteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Agregar Ajuste</h3>
                <button onClick={() => setShowAjusteModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                  <CloseIcon className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            <form onSubmit={handleAgregarAjuste} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Tipo de ajuste *</label>
                <select
                  value={ajusteForm.tipo}
                  onChange={(e) => setAjusteForm(prev => ({ ...prev, tipo: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                >
                  <option value="descuento">Descuento</option>
                  <option value="cortesia">Cortesía</option>
                  <option value="ajuste_comercial">Ajuste comercial</option>
                  <option value="recargo">Recargo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Descripción *</label>
                <input
                  type="text"
                  value={ajusteForm.descripcion}
                  onChange={(e) => setAjusteForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Descuento por cliente frecuente"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">
                  Valor * {ajusteForm.tipo === 'recargo' ? '(se suma)' : '(se resta)'}
                </label>
                <input
                  type="number"
                  value={ajusteForm.valor}
                  onChange={(e) => setAjusteForm(prev => ({ ...prev, valor: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="0"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Motivo</label>
                <input
                  type="text"
                  value={ajusteForm.motivo}
                  onChange={(e) => setAjusteForm(prev => ({ ...prev, motivo: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Razón del ajuste..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAjusteModal(false)}
                  className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Aplicando...' : 'Aplicar Ajuste'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Fecha de Entrega */}
      {showFechaEntregaModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  Fecha de Entrega
                </h3>
                <button onClick={() => setShowFechaEntregaModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                  <CloseIcon className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <p className="text-sm text-zinc-400 mt-2">
                Esta será la fecha que verá el cliente en los documentos.
              </p>
            </div>

            <form onSubmit={handleActualizarFechaEntrega} className="p-6 space-y-4">
              {/* Modalidad */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Modalidad de fecha</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFechaEntregaForm(prev => ({ ...prev, modalidad: 'automatica' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      fechaEntregaForm.modalidad === 'automatica'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="btn-modalidad-automatica"
                  >
                    <Clock className={`w-6 h-6 mx-auto mb-2 ${
                      fechaEntregaForm.modalidad === 'automatica' ? 'text-blue-400' : 'text-zinc-500'
                    }`} />
                    <p className={`text-sm font-medium ${
                      fechaEntregaForm.modalidad === 'automatica' ? 'text-blue-400' : 'text-zinc-400'
                    }`}>
                      Automática
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      8 días antes del evento
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFechaEntregaForm(prev => ({ ...prev, modalidad: 'manual' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      fechaEntregaForm.modalidad === 'manual'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="btn-modalidad-manual"
                  >
                    <Edit className={`w-6 h-6 mx-auto mb-2 ${
                      fechaEntregaForm.modalidad === 'manual' ? 'text-amber-400' : 'text-zinc-500'
                    }`} />
                    <p className={`text-sm font-medium ${
                      fechaEntregaForm.modalidad === 'manual' ? 'text-amber-400' : 'text-zinc-400'
                    }`}>
                      Manual
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Fecha personalizada
                    </p>
                  </button>
                </div>
              </div>

              {/* Fecha manual */}
              {fechaEntregaForm.modalidad === 'manual' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Fecha de entrega *</label>
                  <input
                    type="date"
                    value={fechaEntregaForm.fecha_entrega}
                    onChange={(e) => setFechaEntregaForm(prev => ({ ...prev, fecha_entrega: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required={fechaEntregaForm.modalidad === 'manual'}
                    data-testid="input-fecha-entrega"
                  />
                </div>
              )}

              {/* Info de fecha automática */}
              {fechaEntregaForm.modalidad === 'automatica' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-sm text-blue-300">
                    {orden?.fecha_evento ? (
                      <>
                        La fecha se calculará automáticamente como <strong>8 días antes</strong> del evento 
                        ({formatDate(orden.fecha_evento)}).
                      </>
                    ) : (
                      <>
                        No hay fecha de evento definida. Se recomienda usar fecha manual.
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Ciudad de entrega */}
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Ciudad de entrega (opcional)</label>
                <input
                  type="text"
                  value={fechaEntregaForm.ciudad_entrega}
                  onChange={(e) => setFechaEntregaForm(prev => ({ ...prev, ciudad_entrega: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Bogotá, Medellín..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowFechaEntregaModal(false)}
                  className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                  data-testid="btn-cancelar-fecha"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="btn-guardar-fecha"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Configurar Plan de Pagos */}
      {showConfigPlanModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                  Configurar Plan de Pagos
                </h3>
                <button onClick={() => setShowConfigPlanModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                  <CloseIcon className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <p className="text-sm text-zinc-400 mt-2">
                Total de la orden: <span className="text-white font-medium">{formatPrice(orden?.total_final || orden?.total || 0)}</span>
              </p>
            </div>

            <form onSubmit={handleConfigurarPlan} className="p-6 space-y-4">
              {/* Modalidad de pago */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Modalidad de pago</label>
                <select
                  value={configPlanForm.modalidad_pago}
                  onChange={(e) => setConfigPlanForm(prev => ({ ...prev, modalidad_pago: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="select-modalidad-pago"
                >
                  <option value="pago_total">Contado (100%)</option>
                  <option value="anticipo_saldo">Anticipo + Saldo</option>
                  <option value="pago_plazos">Cuotas</option>
                </select>
              </div>

              {/* Porcentaje anticipo (solo para anticipo_saldo) */}
              {configPlanForm.modalidad_pago === 'anticipo_saldo' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Porcentaje de anticipo</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="30"
                      max="90"
                      step="10"
                      value={configPlanForm.porcentaje_anticipo}
                      onChange={(e) => setConfigPlanForm(prev => ({ ...prev, porcentaje_anticipo: parseInt(e.target.value) }))}
                      className="flex-1"
                    />
                    <span className="text-xl font-bold text-amber-400 w-16 text-center">{configPlanForm.porcentaje_anticipo}%</span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-500 mt-1">
                    <span>Anticipo: {formatPrice((orden?.total_final || orden?.total || 0) * configPlanForm.porcentaje_anticipo / 100)}</span>
                    <span>Saldo: {formatPrice((orden?.total_final || orden?.total || 0) * (100 - configPlanForm.porcentaje_anticipo) / 100)}</span>
                  </div>
                </div>
              )}

              {/* Número de cuotas (solo para pago_plazos) */}
              {configPlanForm.modalidad_pago === 'pago_plazos' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Número de cuotas</label>
                  <select
                    value={configPlanForm.num_cuotas}
                    onChange={(e) => setConfigPlanForm(prev => ({ ...prev, num_cuotas: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={2}>2 cuotas</option>
                    <option value={3}>3 cuotas</option>
                    <option value={4}>4 cuotas</option>
                    <option value={6}>6 cuotas</option>
                  </select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Valor por cuota: {formatPrice((orden?.total_final || orden?.total || 0) / configPlanForm.num_cuotas)}
                  </p>
                </div>
              )}

              {/* Info pago total */}
              {configPlanForm.modalidad_pago === 'pago_total' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <p className="text-sm text-emerald-300">
                    Se creará una única cuota por el total: <strong>{formatPrice(orden?.total_final || orden?.total || 0)}</strong>
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowConfigPlanModal(false)}
                  className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="btn-confirmar-plan"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VentaDetail;
